import React, { useEffect, useState } from "react";
import axiosInstance from "../redux/helpers/axios";

/**
 * @author
 * @function Customer
 **/

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const PAGE_SIZE = 10;

export const Customer = (props) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [cartCustomer, setCartCustomer] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [cartLoading, setCartLoading] = useState(false);
  const [cartError, setCartError] = useState(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(
          `/admin/services/user/get-all`,
          { params: { page, limit: PAGE_SIZE, search: search || undefined } },
        );
        // Expected shape: { users, totalCount, totalPages }
        setCustomers(response?.data?.users || []);
        setTotalCount(response?.data?.totalCount ?? 0);
        setTotalPages(response?.data?.totalPages ?? 1);
      } catch (error) {
        console.error("Error fetching customers:", error);
        setError("Failed to load customers.");
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [page, search]);

  const openCartModal = async (customer) => {
    setCartCustomer(customer);
    setCartModalOpen(true);
    setCartError(null);
    setCartItems([]);
    try {
      setCartLoading(true);
      const response = await axiosInstance.get(
        `/admin/services/cart/get-user-cart-items/${Number(customer.id)}`,
      );
      setCartItems(response?.data?.cartItems || []);
    } catch (error) {
      console.error("Error fetching cart items:", error);
      setCartError("Failed to load cart items.");
    } finally {
      setCartLoading(false);
    }
  };

  const closeCartModal = () => {
    setCartModalOpen(false);
    setCartCustomer(null);
    setCartItems([]);
    setCartError(null);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1); // reset to first page whenever the query changes
  };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
  };

  function getAliCdnUrl(url) {
    const filename = url.split("/").pop().split("?")[0];
    return `https://img.alicdn.com/${filename}`;
  }

  const handleLoginAsCustomer = async (customer) => {
    try {
      const response = await axiosInstance.post(
        `/admin/services/user/login-with-email`,
        {
          email: customer.email,
        },
      );
      const { token, user } = response.data;

      if (!token) {
        throw new Error("No token returned from server");
      }

      // Open alistore.com.bd in a new tab with token + user as one-time params.
      // The target site's bootstrap logic must read + store them, then clean the URL.
      const redirectUrl = new URL("https://alistore.com.bd/auth/impersonate");
      redirectUrl.searchParams.set("token", token);
      redirectUrl.searchParams.set(
        "user",
        encodeURIComponent(JSON.stringify(user)),
      );

      const newTab = window.open(
        redirectUrl.toString(),
        "_blank",
        "noopener,noreferrer",
      );
      if (!newTab) {
        alert(
          "Popup blocked — please allow popups for this site to log in as customer.",
        );
      }
    } catch (error) {
      console.error("Error logging in as customer:", error);
      alert("Failed to log in as customer.");
    }
  };

  return (
    <div className="">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold text-gray-800">
          Customers ({totalCount})
        </h1>
        <input
          type="text"
          value={search}
          onChange={handleSearchChange}
          placeholder="Search by name, email, or phone"
          className="w-72 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-500">
          Loading customers...
        </div>
      )}

      {!loading && error && (
        <div className="rounded-md bg-red-50 text-red-600 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && customers.length === 0 && (
        <div className="flex items-center justify-center py-16 text-gray-500">
          No customers found.
        </div>
      )}

      {!loading && !error && customers.length > 0 && (
        <div>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">
                    Gender
                  </th>

                  <th className="px-4 py-3 text-left font-medium text-gray-600">
                    Wallet
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">
                    Verification
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">
                    Joined
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {customer.name || "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {customer.email || "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 capitalize">
                      {customer.gender || "-"}
                    </td>

                    <td className="px-4 py-3 text-gray-600">
                      {customer?.wallet?.currency}{" "}
                      {customer?.wallet?.balance?.toLocaleString() || "0"}
                    </td>
                    <td className="px-4 py-3">
                      {customer?.verification?.verified ? (
                        <div className="flex flex-col items-center gap-2">
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                            Verified
                          </span>
                          <span>
                            {formatDate(customer.verification.verifiedAt)}
                          </span>
                        </div>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                          Unverified
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatDate(customer.createdAt)}
                    </td>
                    <td className="px-4 py-3 gap-2 flex flex-col">
                      <button
                        onClick={() => openCartModal(customer)}
                        className="rounded-md bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-100"
                      >
                        View Cart
                      </button>

                      <button
                        onClick={() => handleLoginAsCustomer(customer)}
                        className="rounded-md bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-100"
                      >
                        Login as Customer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 1}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Prev
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (p) =>
                      p === 1 || p === totalPages || Math.abs(p - page) <= 1,
                  )
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push("ellipsis");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === "ellipsis" ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-2 text-sm text-gray-400"
                      >
                        &hellip;
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => goToPage(p)}
                        className={`rounded-md px-3 py-1.5 text-sm ${
                          p === page
                            ? "bg-blue-600 text-white"
                            : "border border-gray-300 text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}

                <button
                  onClick={() => goToPage(page + 1)}
                  disabled={page === totalPages}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {cartModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={closeCartModal}
        >
          <div
            className="w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h2 className="text-lg font-semibold text-gray-800">
                Cart Items {cartCustomer?.name ? `- ${cartCustomer.name}` : ""}
              </h2>
              <button
                onClick={closeCartModal}
                className="text-gray-400 hover:text-gray-600"
              >
                &times;
              </button>
            </div>

            <div className="p-5">
              {cartLoading && (
                <div className="flex items-center justify-center py-10 text-gray-500">
                  Loading cart items...
                </div>
              )}

              {!cartLoading && cartError && (
                <div className="rounded-md bg-red-50 text-red-600 px-4 py-3 text-sm">
                  {cartError}
                </div>
              )}

              {!cartLoading && !cartError && cartItems.length === 0 && (
                <div className="flex items-center justify-center py-10 text-gray-500">
                  No items in cart.
                </div>
              )}

              {!cartLoading && !cartError && cartItems.length > 0 && (
                <div className="space-y-3">
                  {cartItems.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-lg border border-gray-200 p-3"
                    >
                      <div className="flex gap-3">
                        {item.store === "A1688" ? (
                          <img
                            src={getAliCdnUrl(item.productImg)}
                            alt={item.productTitle}
                            className="w-20 h-20 rounded-md object-cover flex-shrink-0 bg-gray-100"
                          />
                        ) : (
                          <img
                            src={item.productImg}
                            alt={item.productTitle}
                            className="w-20 h-20 rounded-md object-cover flex-shrink-0 bg-gray-100"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <a
                            href={item.productUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="line-clamp-2 text-sm font-medium text-gray-800 hover:text-blue-600"
                          >
                            {item.productTitle}
                          </a>
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                            <span>Seller: {item.sellerName || "-"}</span>
                            <span>Store: {item.store || "-"}</span>
                            <span>Qty: {item.totalQty ?? "-"}</span>
                            <span className="font-medium text-gray-700">
                              Total: {item.itemTotal ?? "-"}
                            </span>
                          </div>
                          {item.note && (
                            <div className="mt-1 text-xs text-gray-500">
                              Note: {item.note}
                            </div>
                          )}
                        </div>
                      </div>

                      {item.variations?.length > 0 && (
                        <div className="mt-3 overflow-x-auto rounded-md border border-gray-100">
                          <table className="min-w-full divide-y divide-gray-100 text-xs">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left font-medium text-gray-500">
                                  SKU
                                </th>
                                <th className="px-3 py-2 text-left font-medium text-gray-500">
                                  Properties
                                </th>
                                <th className="px-3 py-2 text-left font-medium text-gray-500">
                                  Qty
                                </th>
                                <th className="px-3 py-2 text-left font-medium text-gray-500">
                                  Unit Price
                                </th>
                                <th className="px-3 py-2 text-left font-medium text-gray-500">
                                  Total
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {item.variations.map((variation) => (
                                <tr key={variation.id}>
                                  <td className="px-3 py-2 text-gray-600">
                                    {variation.sku || "-"}
                                  </td>
                                  <td className="px-3 py-2 text-gray-600">
                                    {variation.properties
                                      ?.map(
                                        (prop) => `${prop.name}: ${prop.value}`,
                                      )
                                      .join(", ") || "-"}
                                  </td>
                                  <td className="px-3 py-2 text-gray-600">
                                    {variation.quantity ?? "-"}
                                  </td>
                                  <td className="px-3 py-2 text-gray-600">
                                    {variation.unitPrice ?? "-"}
                                  </td>
                                  <td className="px-3 py-2 text-gray-600">
                                    {variation.totalPrice ?? "-"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
